/**
 * 解析站点配置文本
 * @param {string} text - 配置文本内容
 * @returns {Object} 解析后的配置对象 { siteName, baseUrl, credentials: [], models: [] }
 */
export function parseSiteConfig(text) {
  if (!text || typeof text !== 'string') {
    throw new Error('配置文本不能为空');
  }

  const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
  
  const config = {
    siteName: '',
    baseUrl: '',
    credentials: [],
    models: []
  };

  let currentSection = null;
  let currentCredential = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 解析站点名称（# 开头）
    if (line.startsWith('# ') && !line.includes('Base URL') && !line.includes('绑定凭证') && !line.includes('支持模型')) {
      config.siteName = line.substring(2).trim();
      continue;
    }

    // 解析 Base URL
    if (line.includes('Base URL:')) {
      const match = line.match(/Base URL:\s*`?([^`\s]+)`?/);
      if (match) {
        // 去除末尾的斜杠，避免路径变成 //chat/completions
        config.baseUrl = match[1].trim().replace(/\/+$/, '');
      }
      continue;
    }

    // 检测绑定凭证部分
    if (line.includes('绑定凭证')) {
      currentSection = 'credentials';
      continue;
    }

    // 检测支持模型部分
    if (line.includes('支持模型')) {
      currentSection = 'models';
      continue;
    }

    // 解析凭证部分
    if (currentSection === 'credentials') {
      // 新的凭证项（- 开头，包含邮箱）
      if (line.startsWith('- ') && line.includes('@')) {
        if (currentCredential) {
          config.credentials.push(currentCredential);
        }
        currentCredential = {
          email: line.substring(2).trim(),
          remark: '',
          apiKey: ''
        };
        continue;
      }

      // 备注行
      if (line.startsWith('备注:') && currentCredential) {
        currentCredential.remark = line.substring(3).trim();
        continue;
      }

      // API Key 行
      if (line.startsWith('api_key:') && currentCredential) {
        currentCredential.apiKey = line.substring(8).trim();
        continue;
      }
    }

    // 解析模型部分
    if (currentSection === 'models') {
      if (line.startsWith('- ')) {
        const modelMatch = line.match(/-\s+([^\s\[]+)/);
        if (modelMatch) {
          config.models.push(modelMatch[1].trim());
        }
      }
    }
  }

  // 添加最后一个凭证
  if (currentCredential) {
    config.credentials.push(currentCredential);
  }

  // 验证必要字段
  if (!config.baseUrl) {
    throw new Error('未找到 Base URL');
  }

  if (config.credentials.length === 0) {
    throw new Error('未找到任何凭证');
  }

  if (config.models.length === 0) {
    throw new Error('未找到任何模型');
  }

  return config;
}

/**
 * 将配置转换为渠道数据
 * @param {Object} config - 解析后的配置对象
 * @param {string} group - 分组名称，默认为 'default'
 * @param {number} type - 渠道类型，默认为 8（自定义渠道）
 * @returns {Array} 渠道数据数组
 */
export function configToChannels(config, group = 'default', type = 8) {
  // 按 apiKey 对凭证去重，避免同一个 key 生成多条重复渠道（保留第一个）
  const seenKeys = new Set();
  const uniqueCredentials = config.credentials.filter((cred) => {
    if (!cred.apiKey || seenKeys.has(cred.apiKey)) {
      return false;
    }
    seenKeys.add(cred.apiKey);
    return true;
  });

  const siteName = config.siteName && config.siteName.trim() ? config.siteName.trim() : '导入站点';
  return uniqueCredentials.map((cred, index) => ({
    name: siteName,
    type: type,
    key: cred.apiKey,
    base_url: config.baseUrl,
    models: config.models.join(','),
    group: group,
    // 用于匹配已存在渠道的标识
    _email: cred.email,
    _remark: cred.remark,
    _index: index
  }));
}
